import React from 'react';

/**
 * Types and interfaces for the artifact system.
 *
 * Artifacts are outputs that can be displayed alongside a chat, such as documents,
 * code files, or other generated content. The artifact abstraction allows different
 * chat types to share the same side-by-side layout and rendering logic while
 * producing different types of outputs.
 */

/**
 * Enum representing the different types of artifacts that can be created.
 * Currently only DOCUMENT is supported, but this can be extended to support
 * other artifact types like code files, diagrams, etc.
 */
export enum ArtifactType {
  DOCUMENT = 'DOCUMENT',
  PLAN = 'PLAN',
  SOURCE_FILE = 'SOURCE_FILE',
  CANVAS = 'CANVAS',
}

/**
 * Props for artifact components.
 * @template T The type of data the artifact component renders
 */
export interface ArtifactComponentProps<T> {
  /** The artifact data to render */
  data: T;
  /** Whether the artifact is editable */
  editable: boolean;
  /** Optional callback when the close button is clicked (phone view) */
  onClose?: () => void;
}

/**
 * An artifact instance managed by the useArtifacts hook.
 * @template T The type of data this artifact contains
 */
export interface Artifact<T> {
  /** The type of artifact (e.g., DOCUMENT) */
  artifactType: ArtifactType;
  /** Function to open/activate this artifact */
  open: () => void;
  /**
   * Optional callback to run before sending a message.
   * For documents, this ensures the document is saved before the message is sent.
   */
  beforeSendMessage?: () => Promise<void>;
  /** The artifact data */
  data: T;
  /** Component to render the artifact */
  Component: React.ComponentType<ArtifactComponentProps<T>>;
}

/**
 * Get the display label for an artifact type.
 */
export function getArtifactTypeLabel(artifactType: ArtifactType): string {
  switch (artifactType) {
    case ArtifactType.DOCUMENT:
      return 'Draft document';
    case ArtifactType.PLAN:
      return 'Plan';
    case ArtifactType.SOURCE_FILE:
      return 'Source file';
    case ArtifactType.CANVAS:
      return 'Canvas';
    default:
      return 'Artifact';
  }
}
